<template>
    <Modal
        v-if="productComponent"
        :title="title"
        :padding="isTablet ? 'm' : 's'"
        size="l"
        position="top"
        minHeight="m">
        <div class="product-modal-content">
            <component
                :is="productComponent"
                v-bind="$props"
                :product="product"
                :activeTab="activeTab" />
        </div>
    </Modal>
</template>

<script lang="ts">
    import { computed, defineComponent, watch } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import { ProductType } from '@/api/Cart/cart.interfaces';
    import { ProductModal } from '@/store/general/types';
    import { AnyProduct } from '@shared/interfaces/product';
    import { useScreenSize } from '@shared/helpers/viewport';
    import HotelModal from './Variants/Hotel/Index.vue';
    import FlightModal from './Variants/Flight/Index.vue';
    import FerryModal from './Variants/Ferry/Index.vue';
    import ActivityModal from './Variants/Activity/Index.vue';
    import TransferModal from './Variants/Transfer/Index.vue';
    import Modal from '@ui/components/Modal/Modal.vue';

    export default defineComponent({
        name: 'ProductModal',
        methods: { t },
        components: {
            Modal,
            ActivityModal,
            FerryModal,
            FlightModal,
            HotelModal,
            TransferModal,
        },
        props: {
            productId: {
                type: String,
                required: true,
            },
        },
        setup(props) {
            const { isTablet } = useScreenSize();
            const store = useStore();
            const product = computed<AnyProduct | undefined>(() =>
                store.getters['cart/getProduct'](props.productId)
            );

            const activeTab = computed(() => {
                const modal: ProductModal | null = store.getters['general/getModal'];
                return modal?.activeTab;
            });

            watch(
                () => activeTab.value,
                (newValue, oldValue) => {
                    if (newValue && newValue !== oldValue) {
                        scrollModalToTop();
                    }
                }
            );

            function scrollModalToTop() {
                const selectors = isTablet.value ? '.modal-container' : '.modal-box-content';
                const container: HTMLElement | null = document.querySelector(selectors);
                if (!container) return;

                container.scrollTo({
                    top: 0,
                });
            }

            const productComponent = computed(() => {
                switch (product.value?.Type) {
                    case ProductType.Flight:
                        return 'FlightModal';
                    case ProductType.Hotel:
                        return 'HotelModal';
                    case ProductType.Ferry:
                        return 'FerryModal';
                    case ProductType.Activity:
                        return 'ActivityModal';
                    case ProductType.Transfer:
                        return 'TransferModal';
                    default:
                        return null;
                }
            });

            const title = computed(() => {
                switch (product.value?.Type) {
                    case ProductType.Flight:
                        return t({ key: 'shared.flight_product' });
                    case ProductType.Hotel:
                        return t({ key: 'shared.hotel_product' });
                    case ProductType.Ferry:
                        return t({ key: 'shared.ferry_product' });
                    case ProductType.Activity:
                        return t({ key: 'shared.activity_product' });
                    case ProductType.Transfer:
                        return t({ key: 'shared.transfer_product' });
                    default:
                        return undefined;
                }
            });

            return {
                isTablet,
                product,
                productComponent,
                title,
                activeTab,
            };
        },
    });
</script>
<style lang="scss">
    @forward 'Product';
</style>
